import { resourceLimits } from "worker_threads";
import { EbayListingFormValues } from "../components/ebayListings/EditListing";
import { IPart, IPartCategoryDefault, IPartImageType } from "../models/Part";
import { DateTime } from "luxon";
import { FormatType, IEbayOffer, IPartAspect } from "../models/EbayOffer";
import { computeEbayListingTitle } from "../components/ebayListings/EbayListingTitle";

type checkEbayDisabledProps = {
  part: IPart;
  offer?: IEbayOffer | null;
  partCategoryDefault?: IPartCategoryDefault | null;
  ignoreListingTime?: boolean;
};

export type ebayChecks = {
  error: string;
  isDisabled: boolean;
  allowEdit: boolean;
};

export default function checkEbayDisabled({ part, offer, partCategoryDefault, ignoreListingTime = false }: checkEbayDisabledProps) {

  let result: ebayChecks = {
    error: "",
    isDisabled: true,
    allowEdit: false,
  };

  // Ensure either offer or partCategoryDefault is provided
  // if (!offer && !partCategoryDefault) {
  //   result.error = "Either offer or partCategoryDefault must be provided.";
  //   result.isDisabled = true;
  //   return result;
  // }


  if (part.soldDate) {
    result.error = "This part has been sold and cannot be edited.";
    return result;
  }

  if (!part.categoryId) {
    result.error = "Part category must be set.";
    return result;
  }

  if (offer && part.offers.length === 0 && !partCategoryDefault) {
    result.error = "This part has no listings created.";
    return result;
  }

  //Imported parts can't be relisted - Idea being they've been published before but we don't have the ebay IDs to update the listing, we have syncs now so this shouldn't ever be an issue
  if (part.offers.length > 0 && part.offers[0].publishedOn !== null && part.offers[0].ebayId === null && part.ebayInventoryApiOfferId == null && part.ebayTradingApiItemId == null) {
    result.error = "This part has been imported and cannot be edited.";
    return result;
  }

  // Title validation
  if (offer && (!offer.title || offer.title.length < 1 || offer.title.length > 80)) {
    result.error = "Title must be between 1 and 80 characters.";
    return result;
  }

  if (!offer && partCategoryDefault && !partCategoryDefault.titleStructure) {
    result.error = "Title must be between 1 and 80 characters or default structure must be set.";
    return result;
  }

  if (!offer && partCategoryDefault && partCategoryDefault.titleStructure) {
    var title = computeEbayListingTitle(part);

    if (title && (title?.length < 1 || title?.length > 80)) {
      result.error = "Title must be between 1 and 80 characters.";
      return result;
    }
  }

  //Need at least thumbnail
  if (part.images.length === 0) {
    result.error = "You must upload at least one image.";
    return result;
  }

  // If none of them are set to use on eBay, treat all of them as selected for eBay
  // No validation error needed - we'll treat all images as selected when publishing

  if (ignoreListingTime === false && offer && !offer.listNow) {

    if (!offer.listingStarted) {
      result.error = "Listing start date must be set.";
      return result;
    }

    // Make sure listingStarted is after now
    if (!offer.listingStarted || DateTime.fromISO(offer.listingStarted) < DateTime.now()) {
      result.error = "Listing start date must be in the future.";
      return result;
    }

    // Listing has to start within 3 weeks
    if (DateTime.fromISO(offer.listingStarted) > DateTime.now().plus({ weeks: 3 })) {
      result.error = "Listing start date must be within 3 weeks.";
      return result;
    }
  }

  // Use the appropriate object for format checks
  const formatObject = offer || partCategoryDefault;
  if (!formatObject) {
    result.error = "No valid offer or part category default provided.";
    return result;
  }

  if (formatObject.format === FormatType.Auction) {
    // Make sure auction start price is set
    if ((!formatObject.auctionStartPrice || formatObject.auctionStartPrice <= 0) || (!part.enteredPrice || part.enteredPrice <= 0)) {
      result.error = "Auction start price must be set.";
      return result;
    }
    // Make sure auction reserve price is set
    if (!formatObject.auctionReservePrice || formatObject.auctionReservePrice <= 0) {
      result.error = "Auction reserve price must be set.";
      return result;
    }

    // Make sure auction start price is less than reserve price
    if (formatObject.auctionStartPrice && formatObject.auctionReservePrice && formatObject.auctionStartPrice > formatObject.auctionReservePrice) {
      result.error = "Auction start price must be less than reserve price.";
      return result;
    }
  } else if (formatObject.format === FormatType.FixedPrice) {
    // Check pricing - use offer price if available, otherwise check part.enteredPrice
    let hasValidPrice = false;
    if (offer) {
      hasValidPrice = !!(offer.buyItNowPrice && offer.buyItNowPrice > 0);
    } else {
      hasValidPrice = !!(part.enteredPrice && part.enteredPrice > 0);
    }

    if (!hasValidPrice) {
      result.error = "Buy it now price must be set.";
      return result;
    }

    // Make sure best offer auto accept price is set (or auto accept threshold)
    const hasAutoAcceptPrice = formatObject.autoAcceptPrice && formatObject.autoAcceptPrice > 0;
    const buyItNowPrice = offer?.buyItNowPrice || part.enteredPrice || 0;
    const hasAutoAcceptThreshold = partCategoryDefault?.autoAcceptThreshold && partCategoryDefault.autoAcceptThreshold > 0 && buyItNowPrice > 0;

    if (formatObject.enableBestOffer && !hasAutoAcceptPrice && !hasAutoAcceptThreshold) {
      result.error = "Best offer auto accept price must be set.";
      return result;
    }

    // Make sure best offer is lower than buy it now price
    if (formatObject.enableBestOffer) {
      // If using threshold, calculate the effective price for validation
      let effectiveAutoAcceptPrice = formatObject.autoAcceptPrice;
      if (!hasAutoAcceptPrice && hasAutoAcceptThreshold && partCategoryDefault?.autoAcceptThreshold) {
        effectiveAutoAcceptPrice = buyItNowPrice - ((buyItNowPrice * partCategoryDefault.autoAcceptThreshold) / 100);
      }

      if (effectiveAutoAcceptPrice && effectiveAutoAcceptPrice >= buyItNowPrice) {
        result.error = "Best offer price must be lower than buy it now price.";
        return result;
      }
    }
  }

  // Check policies
  if (!formatObject.fulfillmentPolicyId || formatObject.fulfillmentPolicyId.length === 0 || !formatObject.paymentPolicyId || formatObject.paymentPolicyId.length === 0) {
    result.error = "Fulfillment policy must be set.";
    return result;
  }

  if (!formatObject.returnPolicyId || formatObject.returnPolicyId.length === 0) {
    result.error = "Return policy must be set.";
    return result;
  }

  if (!formatObject.locationKey || formatObject.locationKey.length === 0) {
    result.error = "Location must be set.";
    return result;
  }

  const hasCategoryId = offer ? offer.categoryId : (partCategoryDefault ? partCategoryDefault.ebayCategoryId : null);

  if (!hasCategoryId) {
    result.error = "eBay category needs to be set.";
    return result;
  }

  //If we have an offer, check to make sure we have a value in every required aspect
  //Else check the partCategoryDefault for required aspects
  let requiredAspects: IPartAspect[] = [];

  if (offer) {
    requiredAspects = part.aspects?.filter(aspect => aspect.isRequired);
  } else if (partCategoryDefault) {
    requiredAspects = partCategoryDefault.categoryCustomAspects?.filter(aspect => aspect.isRequired);
  }

  if (requiredAspects && requiredAspects.length > 0) {
    const missingAspects = requiredAspects?.filter((aspect: IPartAspect) => !aspect.value || aspect.value.length === 0);
    if (missingAspects && missingAspects.length > 0) {
      result.error = `Missing required aspects: ${missingAspects.map(a => a.name).join(", ")}`;
      return result;
    }
  }


  //All checks passed
  result.error = "";
  result.isDisabled = false;
  return result;
};




// OG Code

// if (part.soldDate !== null) {
//   setError("This part has been sold and cannot be edited.");
//   setIsDisabled(true);
//   return;
// }

// //Imported parts can't be relisted
// if (part.offers.length > 0 && part.offers[0].publishedOn !== null && part.offers[0].ebayId === null) {
//   setError("This part has been imported and cannot be edited.");
//   setIsDisabled(true);import { IPartAspect } from './../models/EbayOffer';

//   return;
// }

// // Title has to be 1-80 characters
// if (!offer.title || offer.title.length < 1 || offer.title.length > 80) {
//   setError("Title must be between 1 and 80 characters.");
//   setIsDisabled(true);
//   return;
// }

// // Make sure listingStarted is after now
// if (!offer.listingStarted || DateTime.fromISO(offer.listingStarted) < DateTime.now()) {
//   setError("Listing start date must be in the future.");
//   setIsDisabled(true);
//   return;
// }

// // Listing has to start within 3 weeks
// if (DateTime.fromISO(offer.listingStarted) > DateTime.now().plus({ weeks: 3 })) {
//   setError("Listing start date must be within 3 weeks.");
//   setIsDisabled(true);
//   return;
// }

// if (offer.format === FormatType.Auction) {
//   // Make sure auction start price is set
//   if (!offer.auctionStartPrice || offer.auctionStartPrice <= 0) {
//     setError("Auction start price must be set.");
//     setIsDisabled(true);
//     return;
//   }
//   // Make sure auction reserve price is set
//   if (!offer.auctionReservePrice || offer.auctionReservePrice <= 0) {
//     setError("Auction reserve price must be set.");
//     setIsDisabled(true);
//     return;
//   }

//   // Make sure auction start price is less than reserve price
//   if (offer.auctionStartPrice && offer.auctionReservePrice && offer.auctionStartPrice > offer.auctionReservePrice) {
//     setError("Auction start price must be less than reserve price.");
//     setIsDisabled(true);
//     return;
//   }
// } else if (offer.format === FormatType.FixedPrice) {
//   // Make sure buy it now price is set
//   if (!offer.buyItNowPrice || offer.buyItNowPrice <= 0) {
//     setError("Buy it now price must be set.");
//     setIsDisabled(true);
//     return;
//   }

//   // Make sure best offer auto accept price is set
//   if (offer.enableBestOffer && (!offer.autoAcceptPrice || offer.autoAcceptPrice <= 0)) {
//     setError("Best offer auto accept price must be set.");
//     setIsDisabled(true);
//     return;
//   }

//   // Make sure best offer is lower than buy it now price
//   if (offer.enableBestOffer && (!offer.autoAcceptPrice || offer.autoAcceptPrice > offer.buyItNowPrice)) {
//     setError("Best offer price must be lower than buy it now price.");
//     setIsDisabled(true);
//     return;
//   }

//   if (offer.fulfillmentPolicyId == null || offer.fulfillmentPolicyId.length === 0 || offer.paymentPolicyId == null || offer.paymentPolicyId.length === 0) {
//     setError("Fulfillment and payment policies must be set.");
//     setIsDisabled(true);
//     return;
//   }

//   // if (offer.condition > 2000 && (offer.conditionDescription == null || offer.conditionDescription?.length === 0)) {
//   //   setError("Condition description must be set.");
//   //   setIsDisabled(true);
//   //   return;
//   // }
// }

// //Need at least thumbnail
// if (part.images.length === 0) {
//   setError("You must upload at least one image.");
//   setIsDisabled(true);
//   return;
// }

// // Make sure fields have values
// if (offer.categoryId &&
//   offer.fulfillmentPolicyId &&
//   offer.paymentPolicyId &&
//   offer.returnPolicyId &&
//   offer.locationKey
// ) {
//   setError("");
//   setIsDisabled(false);
//   return;
// } else {
//   setError("Please fill out all required fields.");
//   setIsDisabled(true);
//   return;
// }